import { FileText, MousePointerSquareDashed, Ban, AlertTriangle } from 'lucide-react';
import type { ContextScope, DocumentContext } from '../office/context';

interface Props {
  scope: ContextScope;
  onScopeChange: (s: ContextScope) => void;
  preview: DocumentContext | null;
  hidden?: boolean;
}

const OPTIONS: Array<{ key: ContextScope; label: string; icon: React.ReactNode }> = [
  { key: 'selection', label: 'Selection', icon: <MousePointerSquareDashed className="h-3.5 w-3.5" /> },
  { key: 'document',  label: 'Whole doc', icon: <FileText className="h-3.5 w-3.5" /> },
  { key: 'none',      label: 'None',      icon: <Ban className="h-3.5 w-3.5" /> },
];

export function ContextPanel({ scope, onScopeChange, preview, hidden }: Props) {
  if (hidden) return null;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-ink-500">
          Document context
        </span>
        {preview && preview.approxTokens > 6000 && (
          <span className="flex items-center gap-1 text-[11px] text-amber-600">
            <AlertTriangle className="h-3 w-3" />
            ~{preview.approxTokens.toLocaleString()} tokens
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-1 rounded-lg bg-ink-100/50 p-1">
        {OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onScopeChange(opt.key)}
            className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition ${
              scope === opt.key
                ? 'bg-white text-ink-900 shadow-soft'
                : 'text-ink-500 hover:text-ink-700'
            }`}
          >
            {opt.icon}
            {opt.label}
          </button>
        ))}
      </div>

      {preview && scope !== 'none' && (
        <div className="mt-1.5 truncate text-[11px] text-ink-500" title={preview.label}>
          {preview.label}
          {preview.truncated && <span className="ml-1 text-amber-600">· truncated</span>}
        </div>
      )}
    </div>
  );
}
